// Columna derecha de Vendedores: cabecera con contacto y pestañas Desempeño y Ventas.
// Desempeño suma la tendencia de 8 semanas y el mix del periodo; Ventas lista los tickets.
import { useMemo, useState, type ReactNode } from 'react';
import { LayoutGrid, Mail, Moon, Phone, Target, TrendingDown, User } from 'lucide-react';
import { Button, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx, formatDayMx } from '@/lib/dateHelpers';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { formatPhone } from '@/shared/lib/format/phone';
import { soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import { DataTable, UnavailableState, type DataTableColumn } from '@/shared/patterns/DataTable';
import { ContactCard } from '@/shared/patterns/ContactCard';
import { DetailCard, DetailKeyValue, DetailTabs } from '@/shared/patterns/DetailCard';
import { DetailHeader } from '@/shared/patterns/DetailHeader';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { NoSource, NoSourceBlock } from '@/shared/patterns/NoSource';
import { StatusPill, type PillTone } from '@/shared/patterns/Pills';
import { reportPeriodLabel, type ReportPeriod } from '@/shared/patterns/PeriodBand';
import { Sparkline } from '@/shared/patterns/Sparkline';
import {
    CategoryIcon,
    saleProductName,
    SaleTicketProduct,
    ticketHeadline,
    ticketTitle,
    useTicketExpansion,
} from '@/shared/patterns/SaleTicket';
import { uniqueCustomers } from '../../tiendas/lib/storeDetail';
import { MIX_BASE_CATEGORIES, TREND_WEEKS } from '../constants';
import {
    categoryMix,
    categoryMixWithBase,
    networkRank,
    saleCategories,
    sellerBenchmarks,
    storeRank,
    versusNetwork,
    type Benchmark,
    type CategorySlice,
    type RankPosition,
    type SaleCategory,
    type VersusTone,
} from '../lib/sellerDetail';
import {
    daysWithoutSale,
    sellerAlerts,
    type SellerContext,
    type SellerRow,
} from '../lib/sellersTable';
import { money, paymentLabel } from '../lib/sellersFormat';
import { trendDelta, weeklyPlacedSeries, type TrendTone } from '../lib/sellerTrend';
import { SellerAccessActions } from './SellerAccessActions';
import { SellerWalletBadge } from './SellerWalletBadge';
import { RBAC_UNAVAILABLE_REASON, SellerMedal, SellerRolePills } from './SellersTable';

export type SellerDetailTab = 'performance' | 'sales';

/** Por qué no se pueden leer las ventas del vendedor. `ok` es que sí. */
export type SalesBlock = 'ok' | 'noRetailer' | 'noPermission';

const SALES_BLOCKED: Record<Exclude<SalesBlock, 'ok'>, string> = {
    noRetailer:
        'El listado de ventas se consulta por cadena y tu rol abarca más de una. Ábrelo en la pantalla de Ventas.',
    noPermission: 'Tu rol no incluye el listado de ventas, que es de donde sale este bloque.',
};

const STATUS_PILL: Record<string, { label: string; tone: PillTone }> = {
    ACTIVE: { label: 'Activo', tone: 'success' },
    SUSPENDED: { label: 'Suspendido', tone: 'warning' },
    INACTIVE: { label: 'Inactivo', tone: 'neutral' },
};

const VERSUS_TONE: Record<VersusTone, PillTone> = {
    good: 'success',
    bad: 'danger',
    flat: 'neutral',
};

/** El último corte de comisiones cerrado; `available` en falso si el corte no respondió. */
export interface SellerCommissionState {
    available: boolean;
    period: { from: string; to: string } | null;
}

const NPI_NO_SOURCE = 'El NPI a 30 días todavía no tiene fuente';

export interface SellerSalesState {
    block: SalesBlock;
    items: BackofficeSale[] | undefined;
    truncated: boolean;
    isLoading: boolean;
    error: Error | null;
}

function rankLabel(rank: RankPosition | null): string {
    return rank === null ? '—' : `#${String(rank.position)} / ${String(rank.total)}`;
}

/** Teléfono, correo con copiar y wallet, más las acciones del vendedor; un dato ausente dice por qué. */
function ContactLine({ row, retailerId }: { row: SellerRow; retailerId: string | null }) {
    return (
        <div className="flex flex-none flex-col gap-2 border-b border-border bg-card px-4 py-2.5">
            <ContactCard
                icon={Phone}
                label="Teléfono"
                value={row.phone ? formatPhone(row.phone) : null}
                copyValue={row.phone}
                missing={<span className="font-normal text-muted-foreground">Sin registrar</span>}
            />
            <ContactCard
                icon={Mail}
                label="Correo"
                value={row.email}
                copyValue={row.email}
                missing={
                    row.roles === null ? (
                        <NoSource reason={RBAC_UNAVAILABLE_REASON} />
                    ) : (
                        <span className="font-normal text-muted-foreground">Sin registrar</span>
                    )
                }
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
                <SellerWalletBadge row={row} retailerId={retailerId} />
                <SellerAccessActions row={row} retailerId={retailerId} />
            </div>
        </div>
    );
}

function SellerHeader({
    row,
    rows,
    context,
    retailerId,
}: {
    row: SellerRow;
    rows: readonly SellerRow[];
    context: SellerContext;
    retailerId: string | null;
}) {
    const status = row.status === null ? null : STATUS_PILL[row.status];
    return (
        <>
            <DetailHeader
                name={row.name}
                badges={
                    <>
                        <SellerMedal rank={context.ranks.get(row.sellerId)} />
                        {status ? <StatusPill tone={status.tone}>{status.label}</StatusPill> : null}
                        {row.roles !== null && row.roles.length > 0 ? (
                            <SellerRolePills row={row} />
                        ) : null}
                    </>
                }
                subItems={[
                    <span key="code" className="font-mono">
                        {row.shortCode}
                    </span>,
                    row.storeName,
                    row.zoneName,
                    row.memberSince ? `alta ${formatDateMx(row.memberSince)}` : null,
                ]}
                idLabel="Id del vendedor"
                idValue={row.sellerId}
                facts={[
                    {
                        key: 'storeRank',
                        label: 'Lugar tienda',
                        title: 'Lugar por colocación entre los vendedores de su tienda',
                        value: rankLabel(storeRank(row, rows)),
                    },
                    {
                        key: 'networkRank',
                        label: 'Lugar red',
                        title: 'Lugar por colocación entre todos los vendedores',
                        value: rankLabel(networkRank(row, rows)),
                    },
                    {
                        key: 'customers',
                        label: 'Clientes',
                        title: 'Clientes atendidos: distintos en el periodo, sin canceladas',
                        value:
                            row.customersServed === null ? (
                                <NoSource reason="El agregado todavía no trae los clientes atendidos" />
                            ) : (
                                row.customersServed
                            ),
                    },
                    {
                        key: 'noSale',
                        label: 'Días s/venta',
                        title: 'Días desde la última venta dentro del periodo',
                        value: <DaysWithoutSale days={daysWithoutSale(row, context.now)} />,
                    },
                ]}
            />
            <ContactLine row={row} retailerId={retailerId} />
        </>
    );
}

function DaysWithoutSale({ days }: { days: number | null }) {
    if (days === null) return <span className="text-muted-foreground">—</span>;
    const tone = days > 7 ? 'text-danger' : days > 3 ? 'text-warning' : 'text-success';
    return <span className={tone}>{days === 0 ? 'hoy' : days}</span>;
}

function ActiveAlert({
    tone,
    icon,
    children,
}: {
    tone: 'success' | 'warning';
    icon: ReactNode;
    children: ReactNode;
}) {
    return (
        <div
            className={cn(
                'mb-2 flex items-center gap-2.5 rounded-[5px] border-l-[3px] px-3.5 py-2.5 text-[11.5px] font-bold',
                tone === 'success'
                    ? 'border-success bg-success-soft text-success'
                    : 'border-warning bg-warning-soft text-warning',
            )}
        >
            {icon}
            {children}
        </div>
    );
}

function BenchmarkRow({
    label,
    benchmark,
    format,
}: {
    label: string;
    benchmark: Benchmark;
    format: (value: number) => string;
}) {
    const show = (value: number | null) => (value === null ? '—' : format(value));
    const vs = versusNetwork(benchmark.value, benchmark.networkMedian);
    return (
        <div className="grid grid-cols-[84px_1fr_auto] items-center gap-x-2.5 border-b border-border/40 px-3 py-2 last:border-b-0">
            <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
            <span className="text-[15px] font-bold tabular-nums">{show(benchmark.value)}</span>
            <StatusPill tone={VERSUS_TONE[vs.tone]}>{vs.label}</StatusPill>
            <span className="col-span-2 col-start-2 text-[10.5px] tabular-nums text-muted-foreground">
                tienda {show(benchmark.storeMedian)} · red {show(benchmark.networkMedian)}
            </span>
        </div>
    );
}

function CategoryCard({
    icon,
    label,
    salesCount,
    amountCents,
    active = false,
    onClick,
}: {
    icon: ReactNode;
    label: string;
    salesCount: number;
    amountCents: number;
    active?: boolean;
    onClick?: () => void;
}) {
    const body = (
        <>
            <span className="mt-0.5 flex-none text-muted-foreground">{icon}</span>
            <span className="min-w-0 text-left">
                <span className="block text-[9px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                    {label}
                </span>
                <span className="block text-[14px] font-bold tabular-nums">
                    {salesCount}{' '}
                    <span className="text-[10px] font-medium text-muted-foreground">
                        {salesCount === 1 ? 'ticket' : 'tickets'}
                    </span>
                </span>
                <span className="block text-[10.5px] tabular-nums text-muted-foreground">
                    {money(amountCents)}
                </span>
            </span>
        </>
    );
    const className = cn(
        'flex min-w-0 items-start gap-2 rounded-md border px-2.5 py-2',
        active ? 'border-primary/50 bg-muted' : 'border-border bg-muted/20',
        salesCount === 0 && 'opacity-60',
    );
    return onClick ? (
        <button
            type="button"
            aria-pressed={active}
            onClick={onClick}
            className={cn(className, 'hover:bg-accent')}
        >
            {body}
        </button>
    ) : (
        <div className={className}>{body}</div>
    );
}

function SalesUnavailable({ sales }: { sales: SellerSalesState }) {
    if (sales.block !== 'ok') return <NoSourceBlock>{SALES_BLOCKED[sales.block]}</NoSourceBlock>;
    if (sales.isLoading) return <Skeleton lines={3} />;
    return (
        <NoSourceBlock>
            No se pudieron leer las ventas del periodo
            {sales.error ? `: ${sales.error.message}` : '.'}
        </NoSourceBlock>
    );
}

const TREND_TONE: Record<TrendTone, string> = {
    up: 'text-success',
    down: 'text-danger',
    flat: 'text-muted-foreground',
};

/** Colocación semanal de las últimas 8 semanas; no sigue el periodo de la banda. */
function TrendCard({ trend, now }: { trend: SellerSalesState; now: Date }) {
    const series = useMemo(
        () => (trend.items ? weeklyPlacedSeries(trend.items, now) : null),
        [trend.items, now],
    );
    if (series === null) {
        return (
            <DetailCard title="Tendencia">
                <SalesUnavailable sales={trend} />
            </DetailCard>
        );
    }
    if (trend.truncated) {
        return (
            <DetailCard title="Tendencia">
                <NoSourceBlock>
                    Hay más ventas en las últimas {TREND_WEEKS} semanas de las que se leyeron; la
                    serie saldría incompleta.
                </NoSourceBlock>
            </DetailCard>
        );
    }
    const values = series.map((week) => week.placedCents);
    if (values.every((value) => value === 0)) {
        return (
            <DetailCard title="Tendencia">
                <p className="py-2 text-center text-[11px] italic text-muted-foreground">
                    Sin serie histórica
                </p>
            </DetailCard>
        );
    }
    const delta = trendDelta(values);
    return (
        <DetailCard title="Tendencia">
            <div className="flex items-center gap-4">
                <span className="w-16 flex-none text-[9.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                    Últimas {TREND_WEEKS} sem
                </span>
                <Sparkline
                    values={values}
                    label={`Colocación de las últimas ${String(TREND_WEEKS)} semanas`}
                    pointTitles={series.map(
                        (week) =>
                            `${formatDayMx(week.fromDay)} al ${formatDayMx(week.toDay)}: ${money(week.placedCents)}`,
                    )}
                    className="h-12 w-auto min-w-0 flex-1"
                />
                <div className="flex-none text-right">
                    <div className="text-[11px] text-muted-foreground">Colocación</div>
                    <div className="text-[14px] font-bold tabular-nums">
                        {money(values[values.length - 1])}
                    </div>
                    <div className={cn('text-[10.5px] font-semibold', TREND_TONE[delta.tone])}>
                        {/* Compara la última semana contra la primera de la serie. */}
                        {delta.label} vs hace {TREND_WEEKS - 1} sem
                    </div>
                </div>
            </div>
        </DetailCard>
    );
}

/** Tickets y monto por categoría del periodo, con los clientes atendidos en el encabezado. */
function MixCard({ row, sales }: { row: SellerRow; sales: SellerSalesState }) {
    const mix = useMemo(
        () => (sales.items ? categoryMixWithBase(sales.items, MIX_BASE_CATEGORIES) : null),
        [sales.items],
    );
    const fallbackCustomers = sales.items ? uniqueCustomers(sales.items) : null;
    const customers = row.customersServed ?? fallbackCustomers;
    const partial = row.customersServed === null && sales.truncated;
    return (
        <DetailCard
            title="Mix por categoría"
            meta={
                customers === null
                    ? undefined
                    : `${String(customers)}${partial ? '+' : ''} ${customers === 1 && !partial ? 'cliente atendido' : 'clientes atendidos'}`
            }
            footnote={
                mix !== null && sales.truncated
                    ? 'Lectura parcial: hay más ventas en el periodo de las que se leyeron; ábrelas en la pantalla de Ventas.'
                    : undefined
            }
        >
            {mix === null ? (
                <SalesUnavailable sales={sales} />
            ) : (
                <div className="grid grid-cols-[repeat(auto-fit,minmax(112px,1fr))] gap-2">
                    {mix.map((slice) => (
                        <CategoryCard
                            key={slice.category}
                            icon={<CategoryIcon category={slice.category} />}
                            label={slice.label}
                            salesCount={slice.salesCount}
                            amountCents={slice.amountCents}
                        />
                    ))}
                </div>
            )}
        </DetailCard>
    );
}

/** Comisión del último corte cerrado y NPI M30, que no caben en la tabla. */
function CompensationCard({
    row,
    commission,
}: {
    row: SellerRow;
    commission: SellerCommissionState;
}) {
    const cutLabel = commission.period
        ? `corte del ${formatDateMx(commission.period.from)} al ${formatDateMx(commission.period.to)}`
        : 'último corte cerrado';
    return (
        <DetailCard title="Comisión y NPI" meta={cutLabel}>
            {commission.available ? (
                <DetailKeyValue
                    label="Comisión"
                    value={
                        row.commissionCents === null ? (
                            <NoSource reason="El corte no trae comisión para este vendedor" />
                        ) : (
                            money(row.commissionCents)
                        )
                    }
                />
            ) : (
                <DetailKeyValue label="Comisión" note="El corte de comisiones no respondió" />
            )}
            <DetailKeyValue label="NPI M30" note={NPI_NO_SOURCE} />
        </DetailCard>
    );
}

function PerformanceTab({
    row,
    rows,
    context,
    period,
    commission,
    sales,
    trend,
}: {
    row: SellerRow;
    rows: readonly SellerRow[];
    context: SellerContext;
    period: ReportPeriod;
    commission: SellerCommissionState;
    sales: SellerSalesState;
    trend: SellerSalesState;
}) {
    const alerts = sellerAlerts(row, context);
    const benchmarks = sellerBenchmarks(row, rows);
    const integer = (value: number) => String(Math.round(value));
    return (
        <div className="p-3">
            {alerts.topNetwork ? (
                <ActiveAlert tone="success" icon={<Target className="size-4" aria-hidden />}>
                    Top 10% de la red
                </ActiveAlert>
            ) : null}
            {alerts.noSaleDays !== null ? (
                <ActiveAlert tone="warning" icon={<Moon className="size-4" aria-hidden />}>
                    {alerts.noSaleDays} días sin registrar venta
                </ActiveAlert>
            ) : null}
            {alerts.drop ? (
                <ActiveAlert tone="warning" icon={<TrendingDown className="size-4" aria-hidden />}>
                    Colocación cayó más del 20% vs semana anterior
                </ActiveAlert>
            ) : null}

            <DetailCard
                title="Desempeño vs benchmarks de tu unidad"
                meta={reportPeriodLabel(period)}
                flush
            >
                <BenchmarkRow label="Ventas" benchmark={benchmarks.sales} format={integer} />
                <BenchmarkRow
                    label="Colocación"
                    benchmark={benchmarks.placed}
                    format={(value) => money(Math.round(value))}
                />
                <BenchmarkRow
                    label="Ticket prom"
                    benchmark={benchmarks.averageTicket}
                    format={(value) => money(Math.round(value))}
                />
            </DetailCard>
            <TrendCard trend={trend} now={context.now} />
            <CompensationCard row={row} commission={commission} />
            <MixCard row={row} sales={sales} />
        </div>
    );
}

/** Ancho mínimo de la tabla de ventas; más angosta scrollea dentro de su tarjeta. */
const SALES_TABLE_MIN_WIDTH = '420px';

type SaleSortKey = 'date' | 'customer' | 'product' | 'payment' | 'amount';

function saleSortValue(
    sale: BackofficeSale,
    key: SaleSortKey,
    category: SaleCategory | null,
): string | number {
    switch (key) {
        case 'date':
            return sale.soldAt;
        case 'customer':
            return saleCustomerLabel(sale) ?? '';
        case 'product':
            return ticketHeadline(sale, category, saleProductName(sale)).name;
        case 'payment':
            return paymentLabel(sale) ?? '';
        case 'amount':
            return soldCentsOf(sale);
    }
}

function SalesTab({ sales }: { sales: SellerSalesState }) {
    const [category, setCategory] = useState<SaleCategory | null>(null);
    const [sort, setSort] = useState<{ key: SaleSortKey; direction: 'asc' | 'desc' }>({
        key: 'date',
        direction: 'desc',
    });
    const active = useMemo(
        () => sales.items?.filter((sale) => sale.cancelledAt === null),
        [sales.items],
    );
    const mix: CategorySlice[] = useMemo(() => (active ? categoryMix(active) : []), [active]);
    const { expandColumn, tableProps } = useTicketExpansion<BackofficeSale, SaleSortKey>({
        category,
        totalCentsOf: soldCentsOf,
    });

    if (!active) {
        return (
            <div className="p-3">
                <SalesUnavailable sales={sales} />
            </div>
        );
    }

    const filtered =
        category === null
            ? active
            : active.filter((sale) => saleCategories(sale).includes(category));
    const sign = sort.direction === 'asc' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
        const av = saleSortValue(a, sort.key, category);
        const bv = saleSortValue(b, sort.key, category);
        const cmp =
            typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av).localeCompare(String(bv), 'es-MX');
        return cmp * sign;
    });
    const latest = active.reduce<string | null>(
        (max, sale) => (max === null || sale.soldAt > max ? sale.soldAt : max),
        null,
    );
    const totalCents = active.reduce((sum, sale) => sum + soldCentsOf(sale), 0);

    const columns: DataTableColumn<BackofficeSale, SaleSortKey>[] = [
        expandColumn,
        {
            key: 'date',
            label: 'Fecha',
            width: '88px',
            sortKey: 'date',
            cellClassName: () => 'text-muted-foreground',
            cell: (sale) => formatDateMx(sale.soldAt),
        },
        {
            key: 'customer',
            label: 'Cliente',
            width: '84px',
            sortKey: 'customer',
            cellTitle: (sale) => saleCustomerLabel(sale) ?? undefined,
            cell: (sale) => saleCustomerLabel(sale) ?? 'Sin nombre',
        },
        {
            key: 'product',
            label: 'Producto',
            sortKey: 'product',
            cellTitle: (sale) => ticketTitle(sale, saleProductName(sale)),
            cellOverflow: 'wrap',
            cell: (sale) => (
                <SaleTicketProduct
                    sale={sale}
                    category={category}
                    saleName={saleProductName(sale)}
                    saleIcon={<CategoryIcon category={saleCategories(sale)[0]} />}
                    showSku
                />
            ),
        },
        {
            key: 'payment',
            label: 'Forma de pago',
            width: '78px',
            sortKey: 'payment',
            cellOverflow: 'nowrap',
            cell: (sale) => {
                const label = paymentLabel(sale);
                if (label === null) return <span className="text-muted-foreground">—</span>;
                return (
                    <StatusPill
                        tone={label === 'Contado' || label === 'Crédito SK' ? 'neutral' : 'info'}
                    >
                        {label}
                    </StatusPill>
                );
            },
        },
        {
            key: 'amount',
            label: 'Monto',
            width: '82px',
            numeric: true,
            sortKey: 'amount',
            cellClassName: () => 'font-bold',
            cell: (sale) => money(soldCentsOf(sale)),
        },
    ];

    return (
        <div className="p-3">
            <DetailCard title="Filtrar por categoría">
                <div className="grid grid-cols-3 gap-2">
                    <CategoryCard
                        icon={<LayoutGrid className="size-4" aria-hidden />}
                        label="Todos"
                        salesCount={active.length}
                        amountCents={totalCents}
                        active={category === null}
                        onClick={() => {
                            setCategory(null);
                        }}
                    />
                    {mix.map((slice) => (
                        <CategoryCard
                            key={slice.category}
                            icon={<CategoryIcon category={slice.category} />}
                            label={slice.label}
                            salesCount={slice.salesCount}
                            amountCents={slice.amountCents}
                            active={category === slice.category}
                            onClick={() => {
                                setCategory(slice.category);
                            }}
                        />
                    ))}
                </div>
            </DetailCard>
            <DetailCard
                title={`Ventas · ${String(filtered.length)} ${filtered.length === 1 ? 'ticket' : 'tickets'}`}
                meta={
                    category !== null ? (
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => {
                                setCategory(null);
                            }}
                        >
                            × quitar filtro
                        </button>
                    ) : latest ? (
                        `última: ${formatDateMx(latest)}`
                    ) : undefined
                }
                flush
                footnote={
                    sales.truncated
                        ? 'Hay más ventas en el periodo de las que se leyeron; ábrelas en la pantalla de Ventas.'
                        : undefined
                }
            >
                <div className="flex max-h-[420px] min-h-0 flex-col">
                    <DataTable
                        columns={columns}
                        rows={sorted}
                        rowKey={(sale) => sale.saleId}
                        sort={sort}
                        onSortToggle={(key) => {
                            setSort((current) =>
                                current.key === key
                                    ? {
                                          key,
                                          direction: current.direction === 'asc' ? 'desc' : 'asc',
                                      }
                                    : {
                                          key,
                                          direction:
                                              key === 'date' || key === 'amount' ? 'desc' : 'asc',
                                      },
                            );
                        }}
                        {...tableProps}
                        minWidth={SALES_TABLE_MIN_WIDTH}
                        caption="Ventas del vendedor en el periodo."
                        empty={
                            <span className="italic">
                                Sin ventas registradas
                                {category !== null ? ' en la categoría seleccionada' : ''}
                            </span>
                        }
                    />
                </div>
            </DetailCard>
        </div>
    );
}

export function SellerDetailPanel({
    row,
    rows,
    context,
    hasSelection,
    isLoading,
    error,
    onRetry,
    tab,
    onTabChange,
    sales,
    trend,
    period,
    commission,
    retailerId = null,
}: {
    /** La fila del vendedor elegido; `undefined` si no está en la lista del periodo. */
    row: SellerRow | undefined;
    rows: readonly SellerRow[];
    context: SellerContext;
    hasSelection: boolean;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    tab: SellerDetailTab;
    onTabChange: (tab: SellerDetailTab) => void;
    sales: SellerSalesState;
    /** Ventas de las últimas 8 semanas, para la tendencia. */
    trend: SellerSalesState;
    period: ReportPeriod;
    commission: SellerCommissionState;
    /** Cadena del padrón; sin ella no se ofrecen las acciones de acceso. */
    retailerId?: string | null;
}) {
    if (!hasSelection) {
        return (
            <MasterDetailEmpty
                icon={<User className="size-8" aria-hidden />}
                title="Selecciona un vendedor"
                sub="Elige una fila de la izquierda para ver su desempeño en esta unidad."
            />
        );
    }
    if (error) {
        return (
            <UnavailableState
                title="No se pudo cargar el vendedor"
                body={error.message}
                action={
                    <Button variant="outline" size="sm" onClick={onRetry}>
                        Reintentar
                    </Button>
                }
            />
        );
    }
    if (isLoading) {
        return (
            <div className="flex flex-col gap-3 p-3.5">
                <Skeleton className="h-5 w-48" />
                <Skeleton lines={7} />
            </div>
        );
    }
    if (!row) {
        return (
            <MasterDetailEmpty
                icon={<User className="size-8" aria-hidden />}
                title="Este vendedor no está en la lista"
                sub="No aparece en el padrón ni en las ventas del periodo elegido."
            />
        );
    }

    const salesCount = row.salesCount ?? sales.items?.length;
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <SellerHeader row={row} rows={rows} context={context} retailerId={retailerId} />
            <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="bg-card pt-2">
                    <DetailTabs<SellerDetailTab>
                        tabs={[
                            { key: 'performance', label: 'Desempeño' },
                            {
                                key: 'sales',
                                label:
                                    salesCount === undefined
                                        ? 'Ventas'
                                        : `Ventas ${String(salesCount)}`,
                            },
                        ]}
                        active={tab}
                        onChange={onTabChange}
                    />
                </div>
                {tab === 'performance' ? (
                    <PerformanceTab
                        row={row}
                        rows={rows}
                        context={context}
                        period={period}
                        commission={commission}
                        sales={sales}
                        trend={trend}
                    />
                ) : (
                    <SalesTab key={row.sellerId} sales={sales} />
                )}
            </div>
        </div>
    );
}
